'use client'

/**
 * Enterprise Error Handlers and Utilities
 * Note: Logger is initialized via inline script in layout.tsx
 */
import { toast } from 'sonner'

import {
  AppError,
  AuthenticationError,
  AuthorizationError,
  ERROR_CODES,
  type ErrorMetadata,
  type ErrorRecoveryStrategy,
  type ErrorResponse,
  ErrorSeverity,
  NetworkError,
  RateLimitError,
  ValidationError,
  isAppError,
  isAuthError,
  isNetworkError,
  isRetryableError,
} from './error-types'

/**
 * Central error handler for the application
 */
export class ErrorHandler {
  private static metadata: ErrorMetadata = {}
  private static errorQueue: AppError[] = []
  private static isMonitoringEnabled = process.env['NEXT_PUBLIC_ERROR_MONITORING'] === 'true'

  /**
   * Set global error metadata
   */
  static setMetadata(metadata: Partial<ErrorMetadata>) {
    this.metadata = { ...this.metadata, ...metadata }
  }

  /**
   * Handle and process errors
   */
  static handle(
    error: unknown,
    context?: Record<string, any>,
    recovery?: ErrorRecoveryStrategy
  ): AppError {
    // Merge context with metadata before creating error
    const fullContext = this.metadata ? { ...context, ...this.metadata } : context

    const appError = this.normalizeError(error, fullContext)

    // Log error
    this.logError(appError)

    // Monitor error
    if (this.isMonitoringEnabled) {
      this.sendToMonitoring(appError)
    }

    // Apply recovery strategy
    if (recovery) {
      this.applyRecoveryStrategy(appError, recovery)
    }

    // Queue for batch processing if needed
    this.errorQueue.push(appError)

    return appError
  }

  /**
   * Normalize various error types to AppError
   */
  static normalizeError(error: unknown, context?: Record<string, any>): AppError {
    // Already an AppError
    if (isAppError(error)) {
      return error
    }

    // API Response Error
    if (this.isApiError(error)) {
      return this.handleApiError(error, context)
    }

    // Network Error
    if (isNetworkError(error)) {
      return new NetworkError('Network connection failed', context)
    }

    // Standard Error
    if (error instanceof Error) {
      return new AppError(error.message, 500, 'UNKNOWN_ERROR', true, {
        ...context,
        originalError: error.name,
      })
    }

    // Unknown error
    return new AppError('An unexpected error occurred', 500, 'UNKNOWN_ERROR', false, {
      ...context,
      error: String(error),
    })
  }

  /**
   * Check if error is an API error response
   */
  private static isApiError(error: any): boolean {
    return error?.response?.data?.error || error?.data?.error
  }

  /**
   * Handle API error responses
   */
  private static handleApiError(error: any, context?: Record<string, any>): AppError {
    const apiError = error.response?.data?.error || error.data?.error || {}
    const statusCode = error.response?.status || error.status || 500

    // Map to specific error types
    switch (statusCode) {
      case 400:
        return new ValidationError(apiError.message || 'Invalid request', {
          ...context,
          details: apiError.details,
        })
      case 401:
        return new AuthenticationError(apiError.message, context)
      case 403:
        return new AuthorizationError(apiError.message, context)
      case 429:
        const retryAfter = error.response?.headers?.['retry-after']
        return new RateLimitError(retryAfter ? parseInt(retryAfter) : undefined)
      default:
        return new AppError(
          apiError.message || 'Server error',
          statusCode,
          apiError.code || 'API_ERROR',
          true,
          { ...context, details: apiError.details }
        )
    }
  }

  /**
   * Log errors based on severity
   */
  private static logError(error: AppError) {
    const severity = this.getErrorSeverity(error)

    // Create a plain object with all error properties
    const logData = {
      message: error.message || 'No message',
      code: error.code || 'UNKNOWN',
      statusCode: error.statusCode || 0,
      timestamp: error.timestamp || new Date().toISOString(),
      context: error.context || {},
      stack: error.stack || 'No stack trace',
      name: error.name || 'AppError',
      isOperational: error.isOperational,
    }

    // Defensive check: use logger if available, fallback to console
    const log = typeof logger !== 'undefined' ? logger : console

    switch (severity) {
      case ErrorSeverity.CRITICAL:
        log.error('🚨 CRITICAL ERROR:', JSON.stringify(logData, null, 2))
        break
      case ErrorSeverity.HIGH:
        log.error('❌ ERROR:', JSON.stringify(logData, null, 2))
        break
      case ErrorSeverity.MEDIUM:
        log.warn('⚠️ WARNING:', JSON.stringify(logData, null, 2))
        break
      case ErrorSeverity.LOW:
        log.info('ℹ️ INFO:', JSON.stringify(logData, null, 2))
        break
    }
  }

  /**
   * Determine error severity
   */
  private static getErrorSeverity(error: AppError): ErrorSeverity {
    // Critical: System failures, data loss risks
    if (!error.isOperational || error.statusCode >= 500) {
      return ErrorSeverity.CRITICAL
    }

    // High: Auth failures, security issues
    if (isAuthError(error)) {
      return ErrorSeverity.HIGH
    }

    // Medium: Business logic errors, validation
    if (error.statusCode >= 400) {
      return ErrorSeverity.MEDIUM
    }

    // Low: Informational
    return ErrorSeverity.LOW
  }

  /**
   * Send error to monitoring service
   */
  private static sendToMonitoring(error: AppError) {
    // Integration point for Sentry, DataDog, etc.
    if (typeof window !== 'undefined' && window.Sentry) {
      window.Sentry.captureException(error, {
        level: this.getErrorSeverity(error) as any,
        contexts: {
          app: error.context,
        },
        fingerprint: this.generateFingerprint(error),
      })
    }
  }

  /**
   * Generate error fingerprint for grouping
   */
  private static generateFingerprint(error: AppError): string[] {
    return [error.code, error.statusCode.toString(), error.context?.['path'] || 'unknown-path']
  }

  /**
   * Apply recovery strategy
   */
  private static applyRecoveryStrategy(_error: AppError, strategy: ErrorRecoveryStrategy) {
    // Show toast notification
    if (strategy.toast) {
      toast[strategy.toast.type](strategy.toast.message)
    }

    // Redirect if needed
    if (strategy.redirect && typeof window !== 'undefined') {
      window.location.href = strategy.redirect
    }

    // Execute fallback
    if (strategy.fallback) {
      try {
        strategy.fallback()
      } catch (fallbackError) {
        const log = typeof logger !== 'undefined' ? logger : console
        log.error('Fallback failed:', fallbackError)
      }
    }
  }

  /**
   * Get user-friendly error message
   */
  static getUserMessage(error: AppError): string {
    const messages: Record<string, string> = {
      [ERROR_CODES.AUTH_INVALID_CREDENTIALS]: 'Invalid email or password',
      [ERROR_CODES.AUTH_TOKEN_EXPIRED]: 'Your session has expired. Please login again.',
      [ERROR_CODES.AUTH_2FA_REQUIRED]: 'Two-factor authentication is required',
      [ERROR_CODES.AUTH_ACCOUNT_LOCKED]: 'Your account has been locked. Please contact support.',
      [ERROR_CODES.NETWORK_ERROR]: 'Connection failed. Please check your internet connection.',
      [ERROR_CODES.TIMEOUT]: 'Request timed out. Please try again.',
      [ERROR_CODES.SERVICE_UNAVAILABLE]:
        'Service is temporarily unavailable. Please try again later.',
      [ERROR_CODES.VALIDATION_FAILED]: 'Please check your input and try again.',
      [ERROR_CODES.ENROLLMENT_DUPLICATE]: 'This enrollment already exists.',
      [ERROR_CODES.INTERNAL_ERROR]: 'Something went wrong. Please try again.',
    }

    return messages[error.code] || error.message
  }

  /**
   * Format error for API response
   */
  static formatErrorResponse(error: AppError): ErrorResponse {
    const errorObj: any = {
      message: this.getUserMessage(error),
      code: error.code,
      statusCode: error.statusCode,
      timestamp: error.timestamp,
    }

    // Only add optional properties if they have values
    if (error.context?.['requestId']) errorObj.requestId = error.context['requestId']
    if (error.context?.['details']) errorObj.details = error.context['details']
    if (error.context?.['path']) errorObj.path = error.context['path']

    const suggestion = this.getErrorSuggestion(error)
    if (suggestion) errorObj.suggestion = suggestion

    const response: ErrorResponse = {
      success: false,
      error: errorObj,
    }

    if (error.context?.['meta']) {
      ;(response as any).meta = error.context['meta']
    }

    return response
  }

  /**
   * Get suggestion for error resolution
   */
  private static getErrorSuggestion(error: AppError): string | undefined {
    const suggestions: Record<string, string> = {
      [ERROR_CODES.AUTH_TOKEN_EXPIRED]: 'Please login again to continue',
      [ERROR_CODES.AUTH_2FA_INVALID]: 'Check your authenticator app for the correct code',
      [ERROR_CODES.NETWORK_ERROR]: 'Check your internet connection and try again',
      [ERROR_CODES.VALIDATION_FAILED]: 'Review the form fields and correct any errors',
      [ERROR_CODES.TENANT_LIMIT_EXCEEDED]: 'Upgrade your plan to add more tenants',
    }

    return suggestions[error.code]
  }

  /**
   * Clear error queue
   */
  static clearQueue() {
    this.errorQueue = []
  }

  /**
   * Get error queue
   */
  static getQueue(): AppError[] {
    return [...this.errorQueue]
  }
}

/**
 * React Query error handler
 */
export function handleQueryError(error: unknown): void {
  const appError = ErrorHandler.handle(
    error,
    {
      source: 'react-query',
    },
    {
      toast: {
        message: ErrorHandler.getUserMessage(ErrorHandler.normalizeError(error)),
        type: 'error',
      },
    }
  )

  // Re-throw auth errors to trigger re-authentication
  if (isAuthError(appError)) {
    throw appError
  }
}

/**
 * Retry logic with exponential backoff
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: {
    maxAttempts?: number
    initialDelay?: number
    maxDelay?: number
    backoff?: 'linear' | 'exponential'
    shouldRetry?: (error: unknown) => boolean
  } = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoff = 'exponential',
    shouldRetry = isRetryableError,
  } = options

  let attempt = 0
  let delay = initialDelay

  while (attempt < maxAttempts) {
    try {
      return await fn()
    } catch (error) {
      attempt++

      if (attempt >= maxAttempts || !shouldRetry(error)) {
        throw error
      }

      // Calculate delay
      if (backoff === 'exponential') {
        delay = Math.min(delay * 2, maxDelay)
      }

      // Wait before retry
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }

  throw new Error('Max retry attempts reached')
}

/**
 * Global window error handler setup
 */
export function setupGlobalErrorHandlers() {
  if (typeof window === 'undefined') return

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    ErrorHandler.handle(event.reason, {
      source: 'unhandledrejection',
      promise: true,
    })
    event.preventDefault()
  })

  // Handle global errors
  window.addEventListener('error', (event) => {
    ErrorHandler.handle(event.error || event.message, {
      source: 'window.error',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })
  })
}

// Window type augmentation for Sentry
declare global {
  interface Window {
    Sentry?: any
  }
}
